import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';
import CustomButton from 'tailwind-components/Button/Button';
import {
  getSuncareContractType,
  getSuncareContractUrl,
  getSuncareQuestionnaireAnswers,
  isSuncareContractTypeDeterminate
} from 'helpers/suncare/suncare';

const INTEGER_HELPER_TEXT = 'Enter a valid whole number (0 or greater).';

// Returns the {error, helperText} pair used by both integer TextFields below.
function getIntegerFieldValidation(value) {
  if (value === '') return { error: false, helperText: '' };
  const parsed = Number(value);
  const invalid = parsed < 0 || !Number.isInteger(parsed);
  return {
    error: invalid,
    helperText: invalid ? INTEGER_HELPER_TEXT : ''
  };
}

export default function SuncareContractSigningStep({
  formData,
  setAcknowledgement,
  userEmail
}) {
  const answers = getSuncareQuestionnaireAnswers(formData);
  const isDeterminate = isSuncareContractTypeDeterminate(answers);
  const contractType = getSuncareContractType(answers);
  const contractUrl = contractType ? getSuncareContractUrl(contractType) : null;

  const showAgentCount = formData.suncareHasAgency === true;
  const parsedAgentCount = Number(formData.suncareAgentCount);
  const showProjectedPolicies =
    showAgentCount &&
    formData.suncareAgentCount !== '' &&
    Number.isFinite(parsedAgentCount) &&
    parsedAgentCount >= 5;

  const agentCountValidation = getIntegerFieldValidation(
    formData.suncareAgentCount
  );
  const projectedPoliciesValidation = getIntegerFieldValidation(
    formData.suncareProjectedPolicies
  );

  const handleHasAgencyChange = event => {
    const value = event.target.value === 'yes';
    setAcknowledgement('suncareHasAgency', value);
    setAcknowledgement('suncareContractOpened', false);
    if (!value) {
      setAcknowledgement('suncareAgentCount', '');
      setAcknowledgement('suncareProjectedPolicies', '');
    }
  };

  const handleAgentCountChange = event => {
    const value = event.target.value;
    setAcknowledgement('suncareAgentCount', value);
    setAcknowledgement('suncareContractOpened', false);
    const count = Number(value);
    if (value === '' || !Number.isFinite(count) || count < 5) {
      setAcknowledgement('suncareProjectedPolicies', '');
    }
  };

  const handleOpenContract = () => {
    if (contractUrl) {
      window.open(contractUrl, '_blank', 'noopener,noreferrer');
      setAcknowledgement('suncareContractOpened', true);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant='body2' sx={{ mb: 2 }}>
        Answer the questions below before signing your contract.
      </Typography>

      <Alert severity='info' sx={{ mb: 2 }}>
        <Typography variant='body2' fontWeight='bold' gutterBottom>
          After signing in Zoho Sign
        </Typography>
        <Typography variant='body2'>
          Return to this window and click &quot;Send Contract&quot; so your
          contract status gets recorded.
        </Typography>
      </Alert>

      <FormControl component='fieldset' sx={{ width: '100%', mb: 2 }}>
        <Typography variant='body2' fontWeight='medium' sx={{ mb: 1 }}>
          Do you have an Agency?
        </Typography>
        <RadioGroup
          row
          value={
            formData.suncareHasAgency === null
              ? ''
              : formData.suncareHasAgency
                ? 'yes'
                : 'no'
          }
          onChange={handleHasAgencyChange}
        >
          <FormControlLabel value='yes' control={<Radio />} label='Yes' />
          <FormControlLabel value='no' control={<Radio />} label='No' />
        </RadioGroup>
      </FormControl>

      {showAgentCount && (
        <TextField
          label='How many agents do you have under you?'
          type='number'
          value={formData.suncareAgentCount}
          onChange={handleAgentCountChange}
          inputProps={{ min: 0, step: 1 }}
          fullWidth
          required
          sx={{ mb: 2 }}
          error={agentCountValidation.error}
          helperText={agentCountValidation.helperText}
        />
      )}

      {showProjectedPolicies && (
        <TextField
          label='Projected Policies per Year'
          type='number'
          value={formData.suncareProjectedPolicies}
          onChange={event => {
            setAcknowledgement('suncareProjectedPolicies', event.target.value);
            setAcknowledgement('suncareContractOpened', false);
          }}
          inputProps={{ min: 0, step: 1 }}
          fullWidth
          required
          sx={{ mb: 2 }}
          error={projectedPoliciesValidation.error}
          helperText={projectedPoliciesValidation.helperText}
        />
      )}

      {!isDeterminate && formData.suncareHasAgency !== null && (
        <FormHelperText error sx={{ mb: 2 }}>
          Please complete all required questions with valid values to continue.
        </FormHelperText>
      )}

      <Alert severity='warning' sx={{ mb: 2 }}>
        <Typography variant='body2' fontWeight='bold' gutterBottom>
          Sign with your portal email
        </Typography>
        <Typography variant='body2'>
          You must sign the contract using this email address:{' '}
          <strong>{userEmail}</strong>
        </Typography>
      </Alert>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
        <CustomButton
          disabled={!contractUrl}
          size='small'
          variant='primaryLight'
          text='Open Contract in Zoho Sign'
          rounded='small'
          onClick={handleOpenContract}
        />
        {isDeterminate && !formData.suncareContractOpened && (
          <FormHelperText error sx={{ mt: 1, textAlign: 'center' }}>
            Open the contract in Zoho Sign before sending.
          </FormHelperText>
        )}
      </Box>
    </Box>
  );
}

SuncareContractSigningStep.propTypes = {
  formData: PropTypes.object.isRequired,
  setAcknowledgement: PropTypes.func.isRequired,
  userEmail: PropTypes.string
};

SuncareContractSigningStep.defaultProps = {
  userEmail: ''
};
